import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import {onMounted} from "vue";
import router from "@/router";
import jwt_decode from "jwt-decode";
import ErrorHandler from "@/utilities/helpers/ErrorHandler";
import { ErrorLocationType, PreferenceType, SsoType } from "@/utilities/enums/Enums";
import {useGlobalStore} from "@/stores/GlobalStore";
import {UserService} from "@/utilities/services/entities/UserService";
import OAuthService from "@/utilities/services/OAuthService";
import {User} from "oidc-client";
import {UserPreference} from "@/models/user/UserPreference";
import {$t, i18n} from "@/utilities/i18n/config";
import {GeneralResponse} from "@/models/api/Responses";
import {MiscService} from "@/utilities/services/entities/MiscService";
import {notify} from "@kyvg/vue3-notification";
import NullOrUndefinedHelper from "@/utilities/helpers/NullOrUndefinedHelper";
import { anabPolicies } from "@/AppConfig";
import { usePermissionStore } from "@/stores/PermissionStore";
import {PermissionService} from "@/utilities/services/PermissionService";
import {anabClientConfig} from "@/AppConfig";
import { loadCustomLocales } from "@/utilities/i18n/config";


export default /*#__PURE__*/_defineComponent({
  __name: 'OAuthSsoCallback',
  setup(__props) {

const globalStore = useGlobalStore();
const userService = new UserService();
const miscService = new MiscService();
const permissionService = new PermissionService();


const setUserSettings = async(userId:string)=> {
  // get filters for user
  const filterResp = await userService.getUserPreferences(userId, PreferenceType.filter);
  // get hiddenColumns for User
  const columnResp = await userService.getUserPreferences(userId, PreferenceType.hiddenColumn);
  // get general settings for User
  const genSetResp = await userService.getUserPreferences(userId, PreferenceType.general);
  // get columnOrder settings for User
  const colOrderResp = await userService.getUserPreferences(userId, PreferenceType.columnOrder);

  if (filterResp?.statusCode === 0 && columnResp?.statusCode === 0 && genSetResp?.statusCode === 0 && colOrderResp?.statusCode === 0) {
    globalStore.setUserPreferences({
      userFilters: filterResp.preferenceInfo ?? [],
      userHiddenColumns: columnResp.preferenceInfo ?? [],
      userColumnOrder: colOrderResp.preferenceInfo ?? [],
      general: {
        darkMode: genSetResp.preferenceInfo.find((pi: UserPreference) => pi.attributeName === "darkMode")?.attributeValue ?? false,
        userStartPage: genSetResp.preferenceInfo.find((pi: UserPreference) => pi.attributeName === "userStartPage")?.attributeValue ?? "/account",
        userLocale: genSetResp.preferenceInfo.find((pi: UserPreference) => pi.attributeName === "userLocale")?.attributeValue ?? "de",
      },
    });

    // set language. if not null or undefined fallback to DE
    // @ts-expect-error
    i18n.global.locale.value = globalStore.userPreferences.general.userLocale;
  } else {
    ErrorHandler.handleError(new GeneralResponse(6666, "Ihre Einstellungen konnten nicht geladen werden"), ErrorLocationType.table);
  }
}

const setHubNotifs = async()=>{
  const notifResp = await miscService.getHubNotifications();

  if(notifResp?.statusCode === 0){
    globalStore.notifications = notifResp.notifications;
  }
  else{
    ErrorHandler.handleError(
        new GeneralResponse(6667, "Die Benachrichtigungen konnte nicht geladen werden"),
        ErrorLocationType.table
    );
  }
}

const addDynamicRoles= async ()=>{
  const readRoleString = globalStore.settings.disdiSettings?.permissionRead.join(", ");
  const editRoleString = globalStore.settings.disdiSettings?.permissionEdit.join(", ");

  //TODO magic string? better idea?
  permissionService.updateDynamicPolicy("Disdi.Read", readRoleString as string);
  permissionService.updateDynamicPolicy("Disdi.LocationPropertyEdit", editRoleString as string);
}

const getOAuthUser = async (): any => {
    const metaDataUrl = globalStore.settings.startupConfig?.metaDataUrl;
    const metaData = !NullOrUndefinedHelper(metaDataUrl) ? await miscService.getOidcMetaData(metaDataUrl as string) : undefined;    
    const oAuthService = new OAuthService(metaData);

    const userManager = oAuthService.userManager;
    try {
        const user = await userManager.signinRedirectCallback() as User;
        console.log(user);
        return user;
    }
    catch (e) {
        console.error("Login failed:", e)
    }
}

const exchangeCodeForToken = async (code) => {
    const response = await fetch(globalStore.settings.startupConfig?.idsUrl + '/connect/token', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
            grant_type: 'authorization_code',
            code: code,
            redirect_uri: window.location.origin + '/sso/oAuthCallback',
            client_id: anabClientConfig.clientId,
            code_verifier: globalStore.codeVerifier
            // client_secret if public client: omit; otherwise include it
        }),
    });

    const tokenData = await response.json();
    console.log('Tokens:', tokenData);
    return tokenData;
    // Save tokens to localStorage, Vuex, Pinia, etc.
}

onMounted(async () => {
   
  let oAuthUser: { access_token: string }
  if (globalStore.settings.startupConfig.ssoType == SsoType.saml) {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      console.log('The Code is:' + code);
      if (code) {
          oAuthUser = await exchangeCodeForToken(code);
          globalStore.setToken(oAuthUser.access_token);
      }
  }
  else {
      oAuthUser = await getOAuthUser();
  }

  if (oAuthUser == null)
    return;

  try {
    globalStore.setToken(oAuthUser.access_token);

    //@ts-expect-error
    const user = await userService.getIdsUserById(jwt_decode(oAuthUser.access_token).sub, ErrorLocationType.login);

    if (NullOrUndefinedHelper(user)) {
        throw new Error("Forbidden!");        
    }

    globalStore.setUser(user);

    await setUserSettings(user.id);

    await setHubNotifs();

    const permissionStore = usePermissionStore();
    permissionStore.anabPolicies = anabPolicies;

    await addDynamicRoles();

    await globalStore.fetchSystemSettings();

    const returnUrl =
      globalStore.oAuth.routeAfterLogin ||
      globalStore.userPreferences.general.userStartPage;

    loadCustomLocales();
    //@ts-expect-error
    await router.push(returnUrl);

    notify({
      title: "Erfolg",
      text: $t('login.notifySuccessfulLogin'),
      duration: 5000,
      type: "success"
    });
  }
  catch (e){
    console.error("Login failed:", e);
    await router.push("/sso/oAuthError");
  }
})


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, " Waiting "))
}
}

})